import ApiService from '@/plugins/api-plugin/services/api.service'
import AuthService from '@/plugins/api-plugin/services/auth.service'
import {
    ActivityPersonDao,
    ActivityRange,
    Co40PageDao,
    CombinationsQuery,
    PagePeopleDao,
    PageUsageDao,
    PageUsageQuery,
    PeopleQuery,
    RecordViewBody,
    RouteUsersDao,
    SearchCombinationsDao,
    SearchListsDao,
    TimelineQuery,
    UserTimelineDao,
} from '@/interfaces/user-activity/UserActivity.dao'

const BASE = '/user-activity'

/** The Portal activity log (`/user-activity`): page views from the SPA, and the reads of the Usage page and timelines. */
export default class UserActivityService extends ApiService {
    /** POST views - at most 20 page opens; never alerts, a lost batch is not the user's problem. */
    async recordViews(views: RecordViewBody[]): Promise<void> {
        await this.request<{ views: RecordViewBody[] }, void>(
            'POST',
            `${BASE}/views`,
            undefined,
            { views },
            undefined,
            undefined,
            { suppressErrorAlert: true },
        ).catch(() => undefined)
    }

    /** POST views while the page unloads: a `keepalive` fetch outlives the page, an axios request does not. */
    sendViewsOnLeave(views: RecordViewBody[]): void {
        void fetch(`${this.baseURL}${BASE}/views`, {
            method: 'POST',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${AuthService.getAuthToken() || ''}`,
            },
            body: JSON.stringify({ views }),
        }).catch(() => undefined)
    }

    /** GET page-usage - opens per named route and the daily active people in the window, of a system or people. */
    async pageUsage(query: PageUsageQuery, callerName: string): Promise<PageUsageDao> {
        this.cancelRequest([callerName])
        return this.request<undefined, PageUsageDao>(
            'GET',
            `${BASE}/page-usage`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET co40-pages - the catalogued CO Core pages with their titles and menu sections. */
    async co40Pages(callerName: string): Promise<Co40PageDao[]> {
        return this.request<undefined, Co40PageDao[]>(
            'GET',
            `${BASE}/co40-pages`,
            undefined,
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET people - every user by membership name, A to Z, for the People filter. */
    async people(callerName: string): Promise<ActivityPersonDao[]> {
        return this.request<undefined, ActivityPersonDao[]>(
            'GET',
            `${BASE}/people`,
            undefined,
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET page-usage/people - pages used, opens, days active and last open per person, of a kind, group or user. */
    async pagePeople(query: ActivityRange & PeopleQuery, callerName: string): Promise<PagePeopleDao> {
        this.cancelRequest([callerName])
        return this.request<undefined, PagePeopleDao>(
            'GET',
            `${BASE}/page-usage/people`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET page-usage/:route/users - everyone who opens one page, with company and days active, of a kind, group or user. */
    async routeUsers(route: string, query: ActivityRange & PeopleQuery, callerName: string): Promise<RouteUsersDao> {
        this.cancelRequest([callerName])
        return this.request<undefined, RouteUsersDao>(
            'GET',
            `${BASE}/page-usage/${encodeURIComponent(route)}/users`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET searches - the lists with recorded searches in the window, most searched first. */
    async searchLists(query: ActivityRange, callerName: string): Promise<SearchListsDao> {
        this.cancelRequest([callerName])
        return this.request<undefined, SearchListsDao>(
            'GET',
            `${BASE}/searches`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET searches/:search - the filter combinations of one recorded list search, in the given order. */
    async searchCombinations(
        search: string,
        query: CombinationsQuery,
        callerName: string,
    ): Promise<SearchCombinationsDao> {
        this.cancelRequest([callerName])
        return this.request<undefined, SearchCombinationsDao>(
            'GET',
            `${BASE}/searches/${encodeURIComponent(search)}`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }

    /** GET users/:userId - one person's views, actions and searches, newest first. */
    async userTimeline(userId: number, query: TimelineQuery, callerName: string): Promise<UserTimelineDao> {
        return this.request<undefined, UserTimelineDao>(
            'GET',
            `${BASE}/users/${userId}`,
            { ...query },
            undefined,
            undefined,
            callerName,
        )
    }
}
